import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { PawPrint, ArrowDown } from "lucide-react";
import { Logo } from "./Logo";
import { WaitlistForm } from "./WaitlistForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG =
  "https://images.unsplash.com/photo-1537204696486-967f1b7198c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODg5OTIwOHww&ixlib=rb-4.1.0&q=85";

const lineWrap = {
  hidden: {},
  visible: (i) => ({
    transition: { staggerChildren: 0, delayChildren: 0.25 + i * 0.14 },
  }),
};
const line = {
  hidden: { y: "110%" },
  visible: {
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
  },
};

const KineticLine = ({ index, children }) => (
  <motion.span
    className="block overflow-hidden pb-[0.08em] -mb-[0.08em]"
    variants={lineWrap}
    custom={index}
    initial="hidden"
    animate="visible"
  >
    <motion.span className="block will-change-transform" variants={line}>
      {children}
    </motion.span>
  </motion.span>
);

export const Hero = () => {
  const ref = useRef(null);
  const [count, setCount] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    axios.get(`${API}/waitlist/count`).then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  return (
    <header ref={ref} id="top" className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero">
      <motion.div
        style={{ y: bgY }}
        className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full bg-[#FF6B35]/10 blur-3xl pointer-events-none"
      />
      <motion.div
        style={{ y: bgY }}
        className="absolute top-1/2 -left-56 w-[30rem] h-[30rem] rounded-full bg-[#FF6B35]/5 blur-3xl pointer-events-none"
      />

      <nav
        data-testid="hero-nav"
        className="relative z-20 flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6 border-b border-[#1A1A1A]/[0.06]"
      >
        <Logo testId="nav-logo" />
        <div className="flex items-center gap-6">
          <span className="hidden md:flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#5C5C5C]">
            <span className="w-2 h-2 rounded-full bg-[#FF6B35] pulse-dot" />
            Lancio 2026
          </span>
          <motion.a
            href="#unisciti"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            data-testid="nav-cta-button"
            className="bg-[#1A1A1A] hover:bg-[#FF6B35] text-[#F9F8F6] text-sm font-semibold rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            Lista d'attesa
          </motion.a>
        </div>
      </nav>

      <div className="relative z-10 flex-1 grid lg:grid-cols-[1.25fr_1fr] items-center gap-12 lg:gap-8 px-6 sm:px-10 lg:px-16 pt-14 pb-20 lg:pt-8 lg:pb-8">
        <motion.div style={{ opacity: fade }} className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-eyebrow"
            className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#FF6B35] mb-8"
          >
            <PawPrint className="w-4 h-4" />
            La prima app italiana per il tuo cane
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold text-5xl sm:text-7xl lg:text-[6rem] leading-[0.95] tracking-tight text-[#1A1A1A]"
          >
            <KineticLine index={0}>Tutto il mondo</KineticLine>
            <KineticLine index={1}>del tuo cane,</KineticLine>
            <KineticLine index={2}>
              in una <span className="text-[#FF6B35]">famiglia.</span>
            </KineticLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-subtitle"
            className="mt-8 text-base sm:text-lg lg:text-xl leading-relaxed text-[#5C5C5C] max-w-xl"
          >
            Salute, sitter di fiducia e nuovi amici a quattro zampe. FuffyFam connette i
            proprietari di cani in tutta Italia — lascia la tua email e sarai tra i primi a entrare.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10"
          >
            <WaitlistForm idPrefix="hero-waitlist" onJoined={setCount} />
            <p data-testid="hero-waitlist-count" className="mt-4 text-xs sm:text-sm font-medium text-[#5C5C5C]">
              {count !== null && count > 0
                ? `${count} ${count === 1 ? "persona è già" : "persone sono già"} in lista. Niente spam, solo il lancio.`
                : "Niente spam: ti scriveremo solo per il lancio."}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-6 rounded-t-full rounded-b-[3rem] bg-[#FF6B35]/15 -rotate-3" />
            <div className="relative overflow-hidden rounded-t-full rounded-b-[3rem] border border-[#1A1A1A]/10 shadow-[0_30px_80px_rgba(26,26,26,0.15)]">
              <img
                src={HERO_IMG}
                alt="Un labrador felice, il primo membro di FuffyFam"
                data-testid="hero-image"
                className="w-full h-[34rem] object-cover"
              />
            </div>
            <div className="float-slow absolute -bottom-6 -left-10 bg-white rounded-2xl border border-[#1A1A1A]/10 shadow-[0_16px_50px_rgba(26,26,26,0.12)] px-5 py-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FF6B35]">
                <PawPrint className="w-5 h-5 text-white" />
              </span>
              <div>
                <p className="font-display font-bold text-[#1A1A1A] text-sm leading-tight">In arrivo</p>
                <p className="text-xs text-[#5C5C5C]">iOS e Android</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative z-10 hidden lg:flex justify-center pb-8"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 text-[#5C5C5C]"
        >
          <span className="text-[0.65rem] uppercase tracking-[0.3em] font-semibold">Scorri</span>
          <ArrowDown className="w-4 h-4" />
        </motion.div>
      </motion.div>
    </header>
  );
};
